<template>
  <div class="skill-page">
    <!-- ================= 头部 ================= -->
    <div class="header">
      <h1>闭包 Closure</h1>
      <p>
        闭包 = <strong>函数</strong> + <strong>函数创建时所在作用域的引用</strong>。
        即使外层函数已经执行完毕，内层函数依然能记住并访问外层函数的变量。
        在 Vue2 中，闭包无处不在：<code class="header-code">data</code> 必须是函数、
        防抖节流、computed 返回函数、组件复用…… 本页用 6 个可交互案例带你彻底搞懂。
      </p>
    </div>

    <!-- ================= 概念说明 ================= -->
    <div class="concepts">
      <div class="concept-card">
        <span class="tag tag-what">是什么</span>
        <h2>三要素</h2>
        <p>
          ① <code>函数嵌套函数</code>；② 内层函数<code>引用外层变量</code>；
          ③ 内层函数被<code>返回到外部</code>（或作为回调）。
          满足即形成闭包，外层变量因此"活"了下来，不会被垃圾回收。
        </p>
      </div>
      <div class="concept-card">
        <span class="tag tag-how">怎么用</span>
        <h2>核心价值</h2>
        <p>
          让函数<code>记住状态</code>（计数器）、<code>封装私有变量</code>（模块模式）、
          <code>延迟执行</code>（防抖/定时器）。代价是变量常驻内存，
          注意及时释放，避免内存泄漏。
        </p>
      </div>
      <div class="concept-card">
        <span class="tag tag-vue">与 Vue2</span>
        <h2>Vue2 中的身影</h2>
        <p>
          <code>data</code> 必须是函数（每个组件实例各自闭包一份数据）；
          <code>computed</code> 返回函数实现"传参计算"；
          watch / 事件回调 / <code>setInterval</code> 里到处都是闭包。
        </p>
      </div>
    </div>

    <!-- ===== 案例1：计数器闭包 ===== -->
    <div class="case-row">
      <div class="case">
        <div class="case-header">
          <span>① 计数器工厂（闭包保存状态）</span>
          <span class="badge badge-counter">核心概念</span>
        </div>
        <div class="case-body">
          <div class="code-block"><span class="cm">// createCounter 每次调用都会创建一个独立的闭包环境</span>
<span class="kw">function</span> <span class="fn">createCounter</span>() {
  <span class="kw">let</span> <span class="cl">count</span> = <span class="nu">0</span>;   <span class="cm">// ← 外层私有变量，被内层函数"记住"</span>
  <span class="kw">return</span> {
    <span class="fn">increment</span>: <span class="kw">function</span>() { <span class="cl">count</span>++; <span class="kw">return</span> <span class="cl">count</span>; },
    <span class="fn">reset</span>: <span class="kw">function</span>() { <span class="cl">count</span> = <span class="nu">0</span>; }
  };
}</div>
          <div class="demo-tip">两个计数器互不干扰 —— 因为它们是两个独立闭包实例，各自持有自己的 count。</div>
          <div class="counter-box">
            <div class="counter-value">{{ counterA.value }}</div>
            <button class="btn btn-primary" @click="bumpCounterA">计数器 A +1</button>
            <button class="btn btn-ghost" @click="resetCounterA">重置 A</button>
          </div>
          <div class="counter-box second-row">
            <div class="counter-value">{{ counterB.value }}</div>
            <button class="btn btn-success" @click="bumpCounterB">计数器 B +1</button>
            <button class="btn btn-ghost" @click="resetCounterB">重置 B</button>
          </div>
        </div>
      </div>

      <!-- ===== 案例2：data 函数闭包（Vue2 组件隔离原理） ===== -->
      <div class="case">
        <div class="case-header">
          <span>② data 必须是函数（闭包隔离实例）</span>
          <span class="badge badge-data">Vue2 必修</span>
        </div>
        <div class="case-body">
          <div class="code-block"><span class="cm">// Vue2 组件里 data 写成函数，每次实例化</span>
<span class="cm">// 都会调用一次，返回新对象 → 各实例数据互相隔离</span>
<span class="kw">data</span>() {
  <span class="kw">return</span> {
    <span class="cl">count</span>: <span class="nu">0</span>   <span class="cm">// ← 每个组件实例各有一份</span>
  };
}
<span class="cm">// 如果写成 data: { count: 0 }（对象），</span>
<span class="cm">// 所有实例会共享同一份引用，一个变了全都变！</span></div>
          <div class="demo-tip">同一组件被渲染 3 次，每个实例都是独立闭包返回的数据 —— 点一个不影响其他。</div>
          <div class="instance-grid">
            <div class="instance-card" v-for="(inst, i) in instances" :key="i">
              <div class="inst-name">实例 {{ inst.name }}</div>
              <div class="inst-num">{{ inst.count }}</div>
              <button class="inst-btn" @click="inst.count++">点我 +1</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== 案例3：搜索防抖（闭包 + setTimeout） ===== -->
    <div class="case-row">
      <div class="case">
        <div class="case-header">
          <span>③ 输入防抖（闭包缓存定时器）</span>
          <span class="badge badge-debounce">实战场景</span>
        </div>
        <div class="case-body">
          <div class="code-block"><span class="cm">// 闭包把 timer 藏起来，连续输入只执行最后一次</span>
<span class="fn">debounce</span>(<span class="cl">fn</span>, <span class="cl">delay</span> = <span class="nu">500</span>) {
  <span class="kw">let</span> <span class="cl">timer</span> = <span class="kw">null</span>;  <span class="cm">// ← 被返回函数闭包引用</span>
  <span class="kw">return</span> <span class="kw">function</span>(...<span class="cl">args</span>) {
    <span class="fn">clearTimeout</span>(<span class="cl">timer</span>);
    <span class="cl">timer</span> = <span class="fn">setTimeout</span>(() =&gt; <span class="cl">fn</span>.apply(<span class="kw">this</span>, <span class="cl">args</span>), <span class="cl">delay</span>);
  };
}</div>
          <input
            class="search-box"
            v-model="keyword"
            placeholder="输入内容，停止打字 500ms 后才会触发搜索…"
            @input="debouncedSearch"
          />
          <div class="search-result">
            搜索结果：<strong v-if="searched">{{ searched }}</strong>
            <template v-else>（等待输入…）</template>
          </div>
          <div class="log-box">
            <div class="log-line" v-for="(line, i) in logs" :key="i">
              <span class="log-time">{{ line.time }}</span>触发搜索「<strong>{{ line.msg }}</strong>」 <span class="log-count">(第 {{ line.idx }} 次)</span>
            </div>
            <template v-if="!logs.length">日志区：连续打字不会触发，停 500ms 后只触发一次。</template>
          </div>
        </div>
      </div>

      <!-- ===== 案例4：私有变量（模块模式） ===== -->
      <div class="case">
        <div class="case-header">
          <span>④ 钱包（闭包封装私有变量）</span>
          <span class="badge badge-private">模块模式</span>
        </div>
        <div class="case-body">
          <div class="code-block"><span class="cm">// balance 只能通过返回的方法访问 —— 私有变量</span>
<span class="kw">const</span> <span class="cl">wallet</span> = (<span class="kw">function</span>() {
  <span class="kw">let</span> <span class="cl">balance</span> = <span class="nu">100</span>;         <span class="cm">// ← 外部无法直接读写</span>
  <span class="kw">return</span> {
    <span class="fn">deposit</span>: <span class="kw">function</span>(<span class="cl">n</span>) { <span class="cl">balance</span> += <span class="cl">n</span>; },
    <span class="fn">withdraw</span>: <span class="kw">function</span>(<span class="cl">n</span>) {
      <span class="kw">if</span> (<span class="cl">n</span> &gt; <span class="cl">balance</span>) { <span class="fn">alert</span>(<span class="st">'余额不足！'</span>); <span class="kw">return</span>; }
      <span class="cl">balance</span> -= <span class="cl">n</span>;
    },
    <span class="fn">getBalance</span>: <span class="kw">function</span>() { <span class="kw">return</span> <span class="cl">balance</span>; }
  };
})();</div>
          <p class="private-desc">
            当前余额：<code>wallet.getBalance()</code> 只能通过闭包暴露的方法读写，
            外部直接 <code>wallet.balance</code> 得到的是 <code>undefined</code>。
          </p>
          <div class="money-box">
            <span class="money-value">¥ {{ balance }}</span>
            <button class="btn btn-success" @click="deposit">存入 ¥50</button>
            <button class="btn btn-danger" @click="withdraw">取出 ¥30</button>
            <button class="btn btn-ghost" @click="tryHack">试试直接改 balance</button>
          </div>
          <div class="log-box">
            <div class="log-line" v-for="(line, i) in walletLogs" :key="i">
              <span class="log-time">{{ line.time }}</span>{{ line.msg }}
            </div>
            <template v-if="!walletLogs.length">钱包操作日志会显示在这里。</template>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== 案例5：computed 返回函数（传参计算） ===== -->
    <div class="case-row">
      <div class="case">
        <div class="case-header">
          <span>⑤ computed 返回函数（闭包传参）</span>
          <span class="badge badge-computed">Vue2 技巧</span>
        </div>
        <div class="case-body">
          <div class="code-block"><span class="cm">// computed 返回一个函数 → 闭包捕获 this 与数据，</span>
<span class="cm">// 模板里可以像方法一样传参调用，且带缓存依赖追踪</span>
<span class="fn">computed</span>: {
  <span class="fn">scoreLevel</span>() {
    <span class="kw">return</span> (<span class="cl">score</span>) =&gt; {
      <span class="kw">if</span> (<span class="cl">score</span> &gt;= <span class="nu">90</span>) <span class="kw">return</span> <span class="st">'优秀'</span>;
      <span class="kw">if</span> (<span class="cl">score</span> &gt;= <span class="nu">75</span>) <span class="kw">return</span> <span class="st">'良好'</span>;
      <span class="kw">if</span> (<span class="cl">score</span> &gt;= <span class="nu">60</span>) <span class="kw">return</span> <span class="st">'及格'</span>;
      <span class="kw">return</span> <span class="st">'不及格'</span>;
    };
  }
}</div>
          <div class="demo-tip">同一 computed 闭包，对不同参数返回不同等级。分数实时可调：</div>
          <div class="counter-box score-box">
            <span class="score-label">当前分数：</span>
            <div class="counter-value score-value">{{ score }}</div>
            <button class="btn btn-primary" @click="score += 5">+5</button>
            <button class="btn btn-danger" @click="score -= 5">-5</button>
          </div>
          <div class="tag-row">
            <span class="tag-item tag-level">当前等级：{{ scoreLevel(score) }}</span>
            <span class="tag-item tag-fixed" v-for="s in [58, 66, 80, 95]" :key="s">{{ s }} 分 → {{ scoreLevel(s) }}</span>
          </div>
        </div>
      </div>

      <!-- ===== 案例6：定时器闭包（累加器） ===== -->
      <div class="case">
        <div class="case-header">
          <span>⑥ 定时器累加（闭包 + setInterval）</span>
          <span class="badge badge-timer">计时器</span>
        </div>
        <div class="case-body">
          <div class="code-block"><span class="cm">// 闭包让 count 在每次定时回调间保持"记忆"</span>
<span class="fn">startTimer</span>() {
  <span class="kw">let</span> <span class="cl">count</span> = <span class="nu">0</span>;            <span class="cm">// ← 被 setTimeout 回调闭包引用</span>
  <span class="kw">const</span> <span class="cl">tick</span> = <span class="kw">function</span>() {
    <span class="cl">count</span>++;
    <span class="kw">this</span>.<span class="cl">timerVal</span> = <span class="cl">count</span>;   <span class="cm">// 更新 Vue 数据</span>
    <span class="kw">this</span>.<span class="cl">timer</span> = <span class="fn">setTimeout</span>(<span class="cl">tick</span>, <span class="nu">1000</span>);
  }.bind(<span class="kw">this</span>);
  <span class="cl">tick</span>();
}</div>
          <div class="demo-tip">每秒 +1，闭包内部的 count 跨越多次回调持续累加。离开页面记得清理定时器（beforeDestroy）。</div>
          <div class="timer-box">
            <div class="timer-value">{{ timerVal }}</div>
            <span class="timer-unit">秒</span>
            <button class="btn btn-primary" @click="startTimer" :disabled="timerRunning">开始</button>
            <button class="btn btn-danger" @click="stopTimer">停止</button>
            <button class="btn btn-ghost" @click="resetTimer">清零</button>
          </div>
          <div class="log-box timer-log">
            <span>{{ timerRunning ? '⏳ 定时器运行中…' : '⏹ 定时器已停止（闭包环境已释放）' }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== 小结 ===== -->
    <div class="case">
      <div class="case-header"><span>📌 小结：闭包在 Vue2 中的典型应用</span></div>
      <div class="case-body summary-body">
        <p>① <strong>data 函数</strong>：每次实例化调用一次函数 → 每个组件实例拥有独立数据（闭包返回新对象）。</p>
        <p>② <strong>防抖 / 节流</strong>：闭包缓存 timer 变量，实现"停止操作后才执行"。</p>
        <p>③ <strong>computed 返回函数</strong>：闭包捕获 this，实现模板内传参计算的优雅写法。</p>
        <p>④ <strong>模块模式</strong>：IIFE + 闭包封装私有状态（如 Vuex 内部、工具库全局单例）。</p>
        <p>⑤ <strong>定时器 / 事件回调</strong>：回调函数闭包引用外层变量，实现跨调用状态保持。</p>
        <p>⚠️ <strong>注意事项</strong>：闭包变量常驻内存，组件销毁时务必清理定时器 / 事件监听，防止内存泄漏。</p>
      </div>
    </div>

    <div class="footer">Vue2 闭包 Closure 案例 · Vue {{ vueVersion }} · 刷新页面即重置所有状态</div>
  </div>
</template>

<script>
import Vue from 'vue'

// ================= 纯闭包工具（独立于 Vue，演示概念） =================

// ① 计数器工厂：每次调用创建独立闭包
function createCounter() {
  let count = 0
  return {
    increment: function () { count++; return count },
    reset: function () { count = 0; return count }
  }
}

// ④ 钱包：模块模式封装私有变量（模块级单例）
const wallet = (function () {
  let balance = 100
  return {
    deposit: function (n) { balance += n; return balance },
    withdraw: function (n) {
      if (n > balance) { return { ok: false, balance: balance } }
      balance -= n
      return { ok: true, balance: balance }
    },
    getBalance: function () { return balance }
  }
})()

export default {
  name: 'ClosureDemo',
  data() {
    return {
      vueVersion: Vue.version,
      // ① 两个独立闭包实例（createCounter 每次调用创建独立闭包环境）
      counterA: { value: 0, counter: createCounter() },
      counterB: { value: 0, counter: createCounter() },
      // ② data 函数隔离的实例们（用闭包工厂生成）
      instances: (function () {
        // 模拟"组件 data 函数"：每次调用返回全新对象
        function makeData(name) {
          return { name: name, count: 0 }
        }
        return ['A', 'B', 'C'].map(makeData)
      })(),
      // ③ 防抖搜索
      keyword: '',
      searched: '',
      logs: [],
      searchCount: 0,
      // ④ 钱包
      balance: wallet.getBalance(),
      walletLogs: [],
      // ⑤ computed 闭包
      score: 78,
      // ⑥ 定时器
      timerVal: 0,
      timer: null
    }
  },
  computed: {
    // ⑤ computed 返回函数（闭包捕获 this 与数据）
    scoreLevel: function () {
      const self = this // 闭包捕获
      return function (score) {
        if (score >= 90) return '优秀'
        if (score >= 75) return '良好'
        if (score >= 60) return '及格'
        return '不及格'
      }
    },
    timerRunning: function () { return !!this.timer }
  },
  methods: {
    // ① 通过闭包实例的方法操作，并同步到响应式 value
    bumpCounterA: function () {
      this.counterA.value = this.counterA.counter.increment()
    },
    bumpCounterB: function () {
      this.counterB.value = this.counterB.counter.increment()
    },
    resetCounterA: function () {
      this.counterA.value = this.counterA.counter.reset()
    },
    resetCounterB: function () {
      this.counterB.value = this.counterB.counter.reset()
    },
    // ③ 防抖：闭包缓存 timer，连续输入只执行最后一次
    debouncedSearch: (function () {
      let timer = null
      return function () {
        const self = this
        clearTimeout(timer)
        timer = setTimeout(function () {
          if (!self.keyword) return
          self.searchCount++
          self.searched = self.keyword
          const now = new Date()
          const time = now.getHours() + ':' + String(now.getMinutes()).padStart(2, '0') + ':' + String(now.getSeconds()).padStart(2, '0')
          self.logs.unshift({ time: time, msg: self.keyword, idx: self.searchCount })
          if (self.logs.length > 6) self.logs.pop()
        }, 500)
      }
    })(),

    // ④
    deposit: function () {
      const ret = wallet.deposit(50)
      this.balance = ret
      this.pushWalletLog('存入 ¥50，余额 ¥' + ret)
    },
    withdraw: function () {
      const ret = wallet.withdraw(30)
      this.balance = ret.balance
      this.pushWalletLog(ret.ok ? '取出 ¥30，余额 ¥' + ret.balance : '取出失败：余额不足！')
    },
    tryHack: function () {
      // 演示外部无法直接访问私有变量
      const hacked = wallet.balance
      this.pushWalletLog('尝试 wallet.balance = ' + String(hacked) + '（' + (hacked === undefined ? '✅ 访问不到，私有变量保护生效' : '❌ 居然被访问到了') + '）')
    },
    pushWalletLog: function (msg) {
      const now = new Date()
      const time = now.getHours() + ':' + String(now.getMinutes()).padStart(2, '0') + ':' + String(now.getSeconds()).padStart(2, '0')
      this.walletLogs.unshift({ time: time, msg: msg })
      if (this.walletLogs.length > 6) this.walletLogs.pop()
    },

    // ⑥ 定时器：闭包 + setTimeout
    startTimer: function () {
      if (this.timer) return
      let count = 0 // 闭包变量，跨回调保持
      const self = this
      const tick = function () {
        count++
        self.timerVal = count
        self.timer = setTimeout(tick, 1000)
      }
      tick()
    },
    stopTimer: function () {
      clearTimeout(this.timer)
      this.timer = null
    },
    resetTimer: function () {
      clearTimeout(this.timer)
      this.timer = null
      this.timerVal = 0
    }
  },
  beforeDestroy: function () {
    // 释放闭包引用的定时器，防止内存泄漏
    clearTimeout(this.timer)
  },
  deactivated: function () {
    // keep-alive 缓存页面时，切走即停止定时器
    clearTimeout(this.timer)
    this.timer = null
  }
}
</script>

<style scoped>
.header {
  background: linear-gradient(135deg, #2b5876 0%, #4e4376 100%);
  color: #fff;
  padding: 32px 20px;
  text-align: center;
  border-radius: 12px;
  margin-bottom: 24px;
}

.header h1 {
  font-size: 26px;
  font-weight: 700;
  letter-spacing: 2px;
  margin: 0;
}

.header p {
  margin-top: 10px;
  font-size: 14px;
  opacity: 0.85;
  max-width: 680px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.7;
}

.header-code {
  color: #fff;
  background: rgba(255, 255, 255, .2);
  padding: 1px 6px;
  border-radius: 4px;
}

.concepts {
  display: flex;
  gap: 24px;
  max-width: 1200px;
  margin: 0 auto 24px;
  flex-wrap: wrap;
}

.concept-card {
  flex: 1;
  min-width: 260px;
  background: var(--surface-1);
  border-radius: 12px;
  padding: 24px;
  box-shadow: var(--shadow-surface-1);
}

.concept-card h2 {
  font-size: 20px;
  margin-bottom: 8px;
  margin-top: 0;
}

.concept-card .tag {
  display: inline-block;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 10px;
  border-radius: 20px;
  margin-bottom: 10px;
}

.tag-what { background: #fff1f0; color: #f5222d; }
.tag-how { background: #e8f4fd; color: #1890ff; }
.tag-vue { background: #f9f0ff; color: #722ed1; }

.concept-card p {
  font-size: 14px;
  line-height: 1.8;
  color: #555;
  margin: 0;
}

.concept-card code {
  background: var(--surface-2);
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 13px;
  color: #c7254e;
}

.case-row {
  display: flex;
  gap: 24px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.case {
  flex: 1;
  min-width: 340px;
  background: var(--surface-1);
  border-radius: 12px;
  box-shadow: var(--shadow-surface-1);
  overflow: hidden;
}

.case-header {
  padding: 18px 24px;
  font-size: 18px;
  font-weight: 600;
  border-bottom: 1px solid rgba(31, 45, 61, 0.06);
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.case-header .badge {
  font-size: 12px;
  padding: 2px 10px;
  border-radius: 20px;
  font-weight: 600;
}

.badge-counter { background: #fff1f0; color: #f5222d; }
.badge-data { background: #f6ffed; color: #52c41a; }
.badge-debounce { background: #e8f4fd; color: #1890ff; }
.badge-private { background: #fff7e6; color: #fa8c16; }
.badge-computed { background: #f9f0ff; color: #722ed1; }
.badge-timer { background: #e6fffb; color: #13c2c2; }

.case-body {
  padding: 24px;
}

.code-block {
  background: #1e1e2e;
  color: #cdd6f4;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
  font-family: 'SF Mono', Consolas, 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.7;
  overflow-x: auto;
  white-space: pre;
}

.code-block .cm { color: #6c7086; }
.code-block .kw { color: #cba6f7; }
.code-block .fn { color: #89b4fa; }
.code-block .st { color: #a6e3a1; }
.code-block .nu { color: #fab387; }
.code-block .pu { color: #f38ba8; }
.code-block .cl { color: #f9e2af; }

.demo-tip {
  margin-bottom: 12px;
  font-size: 13px;
  color: #888;
}

.counter-box {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.second-row {
  margin-top: 16px;
}

.score-box {
  margin-bottom: 14px;
}

.score-label {
  font-size: 14px;
  color: #555;
}

.btn {
  border: none;
  border-radius: 8px;
  padding: 10px 22px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all .2s;
  color: #fff;
}

.btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, .18);
}

.btn-primary { background: linear-gradient(135deg, #667eea, #764ba2); }
.btn-success { background: linear-gradient(135deg, #43e97b, #38f9d7); color: #166534; }
.btn-warning { background: linear-gradient(135deg, #f7971e, #ffd200); color: #7c4a03; }
.btn-danger { background: linear-gradient(135deg, #f43f47, #c71d6f); }
.btn-ghost {
  background: var(--surface-1);
  color: #666;
  border: 1px solid rgba(31, 45, 61, 0.06);
}

.btn-ghost:hover {
  color: #667eea;
  border-color: #667eea;
}

.btn:disabled {
  opacity: .5;
  cursor: not-allowed;
}

.counter-value {
  font-size: 42px;
  font-weight: 800;
  min-width: 90px;
  text-align: center;
  color: #4e4376;
  background: linear-gradient(135deg, #2b5876, #4e4376);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  /* 等宽数字锁死：计数变化不引起布局抖动 */
  font-variant-numeric: tabular-nums;
  -webkit-font-variant-numeric: tabular-nums;
}

.score-value {
  font-size: 30px;
  /* 等宽数字锁死 */
  font-variant-numeric: tabular-nums;
  -webkit-font-variant-numeric: tabular-nums;
}

.search-box {
  width: 100%;
  padding: 12px 16px;
  font-size: 15px;
  border: 2px solid #e8e8e8;
  border-radius: 8px;
  outline: none;
  transition: border .2s;
  box-sizing: border-box;
}

.search-box:focus {
  border-color: #667eea;
}

.search-result {
  margin-top: 16px;
  padding: 16px;
  background: var(--surface-1);
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.8;
  color: #555;
  min-height: 46px;
}

.search-result strong {
  color: #1890ff;
}

.log-box {
  margin-top: 16px;
  padding: 12px 16px;
  background: var(--surface-1);
  border-radius: 8px;
  font-size: 13px;
  color: #888;
  min-height: 40px;
  font-family: 'SF Mono', Consolas, monospace;
}

.log-box .log-line {
  line-height: 1.9;
}

.log-box .log-time {
  color: #999;
  margin-right: 8px;
}

.log-box .log-count {
  color: #f5222d;
  font-weight: 700;
}

.instance-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
  margin-top: 14px;
}

.instance-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  border-radius: 10px;
  padding: 16px;
  text-align: center;
}

.instance-card .inst-name {
  font-size: 13px;
  opacity: .85;
  margin-bottom: 6px;
}

.instance-card .inst-num {
  font-size: 30px;
  font-weight: 800;
  /* 等宽数字锁死：实例数量变化不引起布局抖动 */
  font-variant-numeric: tabular-nums;
  -webkit-font-variant-numeric: tabular-nums;
}

.instance-card .inst-btn {
  margin-top: 10px;
  background: rgba(255, 255, 255, .2);
  border: none;
  border-radius: 6px;
  color: #fff;
  padding: 6px 14px;
  font-size: 13px;
  cursor: pointer;
  transition: background .2s;
}

.instance-card .inst-btn:hover {
  background: rgba(255, 255, 255, .35);
}

.private-desc {
  font-size: 14px;
  color: #555;
  line-height: 1.9;
  margin-bottom: 16px;
}

.private-desc code {
  background: var(--surface-2);
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 13px;
  color: #c7254e;
}

.money-box {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}

.money-value {
  font-size: 28px;
  font-weight: 800;
  color: #fa8c16;
}

.tag-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
}

.tag-item {
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
}

.tag-level {
  background: #4e4376;
  color: #fff;
}

.tag-fixed {
  background: #52c41a;
}

.timer-box {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}

.timer-value {
  font-size: 34px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: #13c2c2;
  min-width: 60px;
  text-align: center;
}

.timer-unit {
  font-size: 13px;
  color: #999;
}

.timer-log {
  margin-top: 14px;
}

.summary-body {
  font-size: 14px;
  line-height: 2.1;
  color: #555;
}

.summary-body p {
  margin: 0;
}

.footer {
  text-align: center;
  padding: 24px 20px 10px;
  color: #999;
  font-size: 13px;
}
</style>
